import { forwardRef, memo, useState } from 'react'
import cn from 'classnames'

import { FloatingLabel } from './components/FloatingLabel'
import { Text } from '~/shared/ui/Text'

import { TEXT_INPUT_HEIGHT } from './config'
import type { TextInputProps } from './types'

import styles from './TextInput.module.scss'

/**
 * Текстовое поле, спека 01 §24-25. Счётчик символов встроен сюда (не в Field) —
 * ему нужен доступ к value.length, а Field ничего не знает о контроле внутри children.
 *
 * Плавающий лейбл (основатель, п.10) и рамка фокуса (п.9) — см. `TextInput.module.scss`
 * и `./components/FloatingLabel`, которым эта механика переиспользуется.
 *
 * Корень — `<label>`, и это не про доступность, а про попадание пальцем. Само поле высотой
 * 54/60px, а нативный `<input>` внутри — ровно одна строка (~19px): он прижат к низу зоны,
 * чтобы над ним поместилась плавающая подпись. Всё, что выше строки, — почти половина
 * видимого поля, и там до этой правки не было ничего нажимаемого: тап приходился в `div`
 * и не делал ничего. Отсюда «не с первого клика фокусится» с устройства основателя: подпись
 * в покое стоит по центру зоны, её верхняя половина висит ровно над мёртвой областью.
 * `<label>` отдаёт весь прямоугольник поля инпуту и заодно выводит тап-таргет за 44px
 * (правило 12). Кнопки и ссылки в `slotRight` от этого не страдают: по спецификации HTML
 * активация лейбла не срабатывает на нажатиях по интерактивным потомкам.
 */
export const TextInput = memo(
  forwardRef<HTMLInputElement, TextInputProps>(function TextInput(props, ref) {
    const {
      value,
      onChange,
      placeholder,
      size = 'M',
      hasError = false,
      maxLength,
      showCounter = false,
      isDisabled = false,
      autoFocus = false,
      inputMode,
      type = 'text',
      slotRight,
      ariaLabel,
    } = props

    const [isFocused, setIsFocused] = useState(false)
    const isFloating = isFocused || value.length > 0

    return (
      <label
        className={cn(
          styles.root,
          styles[`size_${size}`],
          isFocused && styles.isFocused,
          hasError && styles.hasError,
          isDisabled && styles.isDisabled,
        )}
        style={{ height: TEXT_INPUT_HEIGHT[size] }}
      >
        <div className={cn(styles.controlArea, placeholder && styles.hasFloatingLabel)}>
          {placeholder && (
            <FloatingLabel
              text={placeholder}
              isFloating={isFloating}
              isFocused={isFocused}
              hasError={hasError}
            />
          )}
          <input
            ref={ref}
            className={styles.input}
            value={value}
            maxLength={maxLength}
            disabled={isDisabled}
            autoFocus={autoFocus}
            inputMode={inputMode}
            type={type}
            aria-label={ariaLabel ?? placeholder}
            onChange={(event) => onChange(event.target.value)}
            onFocus={() => setIsFocused(true)}
            onBlur={() => setIsFocused(false)}
          />
        </div>
        {showCounter && maxLength !== undefined && (
          <Text variant='CAPTION' color='MUTED'>
            {value.length}/{maxLength}
          </Text>
        )}
        {slotRight}
      </label>
    )
  }),
)
